import { useEffect, RefObject, MutableRefObject } from 'react'
import * as echarts from 'echarts'
import { safeChartInit } from '../../../../../utils/echarts'
import {
  type AltimetryData,
  transformForECharts3D,
  calculateChartRanges,
  createVisualMapConfig,
  createGrid3DConfig,
} from '../../utils'
import type { GeoAnalysisSectionTranslations } from '../../GeoAnalysisSection.types'

interface UseTopographyChartParams {
  chartRef: RefObject<HTMLDivElement>
  activeTab: string
  altimetryData: AltimetryData | null
  altimetryLoading: boolean
  translations?: GeoAnalysisSectionTranslations
  chartInstanceRef?: MutableRefObject<echarts.ECharts | null>
}

/**
 * Hook to manage 3D topography chart lifecycle
 * Initializes and cleans up the ECharts instance for topography visualization
 */
export const useTopographyChart = ({
  chartRef,
  activeTab,
  altimetryData,
  altimetryLoading,
  translations,
  chartInstanceRef,
}: UseTopographyChartParams) => {
  useEffect(() => {
    if (activeTab !== 'topography' || !chartRef.current) return

    const chart = safeChartInit(chartRef.current)
    if (chartInstanceRef) chartInstanceRef.current = chart

    // Chart labels with translations
    const xAxisLabel = translations?.charts?.xAxis || 'X (meters)'
    const yAxisLabel = translations?.charts?.yAxis || 'Y (meters)'
    const altitudeLabel = translations?.charts?.altitude || 'Altitude (meters)'
    const noDataLabel = translations?.noData || 'No data'

    // Use real altimetry data if available
    if (altimetryData && altimetryData.altimetry_grid.length > 0) {
      const {
        xAxis,
        yAxis,
        data: surfaceData,
        min,
        max,
      } = transformForECharts3D(altimetryData)

      // Calculate ranges and normalization
      const {
        normalizedXAxis,
        normalizedYAxis,
        xRange,
        yRange,
        zRange,
        maxRange,
        viewDistance,
      } = calculateChartRanges(xAxis, yAxis, min, max)

      const option = {
        animation: false,
        tooltip: {
          trigger: 'item',
          formatter: (params: any) => {
            if (params.value && params.value.length >= 3) {
              return `X: ${params.value[0].toFixed(1)} m<br/>Y: ${params.value[1].toFixed(1)} m<br/>${altitudeLabel}: ${params.value[2].toFixed(1)} m`
            }
            return noDataLabel
          },
        },
        visualMap: createVisualMapConfig(min, max),
        xAxis3D: {
          type: 'category',
          name: xAxisLabel,
          nameTextStyle: { fontSize: 12 },
          data: normalizedXAxis,
          axisLabel: {
            show: true,
            interval: Math.floor(normalizedXAxis.length / 5),
            formatter: (value: any) => `${Number(value).toFixed(0)}m`,
          },
        },
        yAxis3D: {
          type: 'category',
          name: yAxisLabel,
          nameTextStyle: { fontSize: 12 },
          data: normalizedYAxis,
          axisLabel: {
            show: true,
            interval: Math.floor(normalizedYAxis.length / 5),
            formatter: (value: any) => `${Number(value).toFixed(0)}m`,
          },
        },
        zAxis3D: {
          type: 'value',
          name: '',
          min: min,
          max: max,
          axisLabel: { show: false },
          axisTick: { show: false },
        },
        grid3D: createGrid3DConfig(viewDistance, {
          width: xRange > 0 ? 100 : 80,
          height: zRange > 0 ? (zRange / maxRange) * 100 : 20,
          depth: yRange > 0 ? 100 : 80,
        }),
        series: [
          {
            type: 'surface',
            wireframe: { show: false },
            shading: 'color',
            data: surfaceData
              .map((row, i) => row.map((value, j) => [j, i, value]))
              .flat(),
          },
        ],
      }

      chart.setOption(option)
    } else if (!altimetryLoading) {
      // No altimetry data available, chart remains empty
      chart.clear()
    }

    // Cleanup
    return () => {
      if (chartInstanceRef) chartInstanceRef.current = null
      if (chart && !chart.isDisposed()) {
        chart.dispose()
      }
    }
  }, [
    activeTab,
    chartRef,
    altimetryData,
    altimetryLoading,
    translations,
    chartInstanceRef,
  ])
}
